import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { ItemDeTerreno } from "@/lib/terreno";
import { cn } from "@/lib/utils";

import {
  conteoDeRegistrosDelItem,
  esBorradorDeTerreno,
  etiquetaDelCruceDeAgua,
  type ClaseDeAgua,
  type OpcionParaVincular,
} from "./aguasTerreno";
import type { ModoDelTerreno } from "./cuadroDeConstrucciones";
import { BloqueDeRegistros, ContadorDeRegistros } from "./RegistrosDeTerrenoDeLaFila";

interface LineaDeTerrenoDelAguaProps {
  informeId: number;
  clase: ClaseDeAgua;
  /** "la fuente 2": completa los nombres accesibles. */
  que: string;
  fila: { id: number | null; terreno_ref: string | null };
  /** Cómo se llama la fila en el encabezado del registro. */
  titulo: string;
  item: ItemDeTerreno | null;
  modo: ModoDelTerreno;
  abierto: boolean;
  onAlternar: () => void;
  /** Con qué filas guardadas puede decir "es la misma que". */
  opciones: OpcionParaVincular[];
  onVincular: (clave: string) => void;
  onAbrirFoto: (uuid: string) => void;
  /** Quien fue a terreno: sus iniciales encabezan el registro, como en el diseño. */
  encargado: string | null;
}

/**
 * La línea de debajo de cada fila de aguas: de dónde viene (insignia
 * "Terreno"), cuánto trajo la visita (contador que abre el registro) y, si la
 * fila nació en la App y todavía no se guardó, qué falta y con qué fila ya
 * cargada se puede confundir.
 *
 * Es de LECTURA: el registro de terreno propone y nunca copia nada a la fila.
 */
export function LineaDeTerrenoDelAgua({
  informeId,
  clase,
  que,
  fila,
  titulo,
  item,
  modo,
  abierto,
  onAlternar,
  opciones,
  onVincular,
  onAbrirFoto,
  encargado,
}: LineaDeTerrenoDelAguaProps) {
  const conteo = conteoDeRegistrosDelItem(item);
  const etiqueta = etiquetaDelCruceDeAgua(modo, conteo);
  const deTerreno = fila.terreno_ref !== null;
  const pideDatos = esBorradorDeTerreno(fila);
  const lista = clase === "fuente" ? "fuente" : "obra";

  // Una fila hecha a mano, sin terreno que cruzar (módulo apagado) y sin
  // enlace, no tiene nada que decir: no se dibuja una línea vacía.
  if (!deTerreno && modo === "oculto") return null;
  // Una fila a medio escribir en la pantalla (sin id ni terreno) tampoco.
  if (fila.id === null && !deTerreno) return null;

  return (
    <>
      <div className="flex flex-wrap items-center gap-2.5 px-3 pb-2">
        {deTerreno ? (
          <span
            title="Nació en la App de terreno"
            className="bg-info-soft text-info-soft-foreground border-info-dot/30 inline-flex items-center gap-1.5 rounded-sm border px-2 py-0.5 text-[0.625rem] font-extrabold tracking-[0.05em] uppercase"
          >
            <span aria-hidden="true" className="bg-info-dot size-1.5 rounded-full" />
            Terreno
          </span>
        ) : null}

        {conteo.total > 0 ? (
          <ContadorDeRegistros
            conteo={conteo}
            abierto={abierto}
            etiqueta={`Registro de terreno de ${que}`}
            onAlternar={onAlternar}
          />
        ) : etiqueta ? (
          <span
            className={cn(
              "rounded-sm px-[0.4375rem] py-0.5 text-[0.6875rem] font-bold",
              etiqueta === "Visita pendiente"
                ? "bg-warning-soft text-warning-soft-foreground"
                : "bg-muted text-ink-muted",
            )}
          >
            {etiqueta}
          </span>
        ) : null}

        {pideDatos ? (
          <span className="text-warning-soft-foreground text-[0.71875rem] font-semibold">
            Completa los datos con sus fotos y audios
          </span>
        ) : null}

        {pideDatos && opciones.length > 0 ? (
          <label className="text-ink-body ml-auto inline-flex items-center gap-2 text-[0.71875rem] font-bold">
            Es la misma que
            <Select value="" onValueChange={onVincular}>
              <SelectTrigger
                size="sm"
                className="max-w-60 rounded-[0.5625rem] px-2 text-xs font-semibold"
                aria-label={`Es la misma que, ${que}`}
              >
                <SelectValue placeholder={`Elegir ${lista}…`} />
              </SelectTrigger>
              <SelectContent>
                {opciones.map((opcion) => (
                  <SelectItem key={opcion.clave} value={opcion.clave}>
                    {opcion.etiqueta}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
        ) : null}
      </div>

      {abierto && item ? (
        <BloqueDeRegistros
          informeId={informeId}
          item={item}
          titulo={titulo}
          encargado={encargado}
          onAbrirFoto={onAbrirFoto}
        />
      ) : null}
    </>
  );
}
